import React, { Component, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Header } from '@components/layout/Header';
import { Footer } from '@components/layout/Footer';
import { MainDashboard } from '@pages/MainDashboard';
import { LoginPage } from '@pages/LoginPage';
import { HomePage } from '@pages/HomePage';
import { RegisterPage } from '@pages/RegisterPage';
import { QQCallbackPage } from '@pages/QQCallbackPage';
import { GuestPage } from '@pages/GuestPage';
import { VerifyEmailPage } from '@pages/VerifyEmailPage';
import { ForgotPasswordPage } from '@pages/ForgotPasswordPage';
import { ResetPasswordPage } from '@pages/ResetPasswordPage';
import { PrivacyPage } from '@pages/PrivacyPage';
import { TermsPage } from '@pages/TermsPage';
import { ContactPage } from '@pages/ContactPage';
import { ProtectedRoute } from '@components/features/ProtectedRoute';
import { Spinner } from '@components/ui/Spinner';

// Lazy load pages for code splitting
const AISelectPage = lazy(() => import('@pages/AISelectPage').then(m => ({ default: m.AISelectPage })));
const ImageConfigPage = lazy(() => import('@pages/ImageConfigPage').then(m => ({ default: m.ImageConfigPage })));
const SamplingPage = lazy(() => import('@pages/SamplingPage').then(m => ({ default: m.SamplingPage })));
const TestingPage = lazy(() => import('@pages/TestingPage').then(m => ({ default: m.TestingPage })));
const ProfilePage = lazy(() => import('@pages/ProfilePage').then(m => ({ default: m.ProfilePage })));
const InvitationPage = lazy(() => import('@pages/InvitationPage').then(m => ({ default: m.InvitationPage })));
const DigitSelectPage = lazy(() => import('@pages/DigitSelectPage').then(m => ({ default: m.DigitSelectPage })));
const DigitConfigPage = lazy(() => import('@pages/DigitConfigPage').then(m => ({ default: m.DigitConfigPage })));
const DigitSamplingPage = lazy(() => import('@pages/DigitSamplingPage').then(m => ({ default: m.DigitSamplingPage })));
const DigitTestingPage = lazy(() => import('@pages/DigitTestingPage').then(m => ({ default: m.DigitTestingPage })));

// Ticket pages
const TicketListPage = lazy(() => import('@pages/TicketListPage').then(m => ({ default: m.TicketListPage })));
const TicketCreatePage = lazy(() => import('@pages/TicketCreatePage').then(m => ({ default: m.TicketCreatePage })));
const TicketDetailPage = lazy(() => import('@pages/TicketDetailPage').then(m => ({ default: m.TicketDetailPage })));
const AdminTicketListPage = lazy(() => import('@pages/AdminTicketListPage').then(m => ({ default: m.AdminTicketListPage })));
const AdminTicketDetailPage = lazy(() => import('@pages/AdminTicketDetailPage').then(m => ({ default: m.AdminTicketDetailPage })));

// Announcement pages
const NotificationCenterPage = lazy(() => import('@pages/NotificationCenterPage').then(m => ({ default: m.NotificationCenterPage })));
const AdminAnnouncementListPage = lazy(() => import('@pages/AdminAnnouncementListPage').then(m => ({ default: m.AdminAnnouncementListPage })));
const AdminAnnouncementFormPage = lazy(() => import('@pages/AdminAnnouncementFormPage').then(m => ({ default: m.AdminAnnouncementFormPage })));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <Spinner size="lg" />
      <p className="mt-4 text-gray-600">加载中...</p>
    </div>
  </div>
);

// ErrorBoundary for catching lazy-load and render failures
interface ErrorBoundaryProps {
  children: React.ReactNode;
}
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}
class LazyErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[LazyErrorBoundary]', error, info.componentStack);
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      const isChunkError = /loading (css |chunk |module )/i.test(this.state.error?.message || '')
        || /failed to fetch dynamically imported module/i.test(this.state.error?.message || '');
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
          <div className="text-center max-w-lg">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">&#x26A0;</span>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {isChunkError ? '页面资源加载失败' : '页面渲染出错'}
            </h3>
            <p className="text-sm text-gray-600 mb-2">
              {isChunkError
                ? '网络不稳定导致页面资源加载失败，请检查网络后重试。'
                : '页面运行时发生错误，请尝试刷新页面。'}
            </p>
            {/* Always show error details for debugging */}
            <div className="bg-gray-100 rounded-lg p-3 mb-6 text-left max-h-32 overflow-auto">
              <p className="text-xs text-red-600 font-mono break-all">
                {this.state.error?.message || 'Unknown error'}
              </p>
              {this.state.error?.stack && (
                <pre className="text-xs text-gray-500 mt-2 whitespace-pre-wrap">
                  {this.state.error.stack.split('\n').slice(1, 4).join('\n')}
                </pre>
              )}
            </div>
            <div className="flex items-center justify-center gap-4">
              <button
                onClick={this.handleReload}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                重试
              </button>
              <button
                onClick={() => window.location.href = '/main'}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                返回首页
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export const AppRoutes: React.FC = () => {
  return (
    <Router>
      <Routes>
        {/* 全屏页面：不显示 Header/Footer */}
        <Route path="/" element={<HomePage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/guest" element={<GuestPage />} />
        <Route path="/verify-email/:uidb64/:token/" element={<VerifyEmailPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password/:uidb64/:token/" element={<ResetPasswordPage />} />
        <Route path="/login/qq/callback" element={<QQCallbackPage />} />

        {/* 带 Header/Footer 的页面 */}
        <Route
          path="*"
          element={
            <div className="min-h-screen flex flex-col bg-gray-50">
              <Header />

              <main className="flex-grow">
                <Routes>
                  {/* 公共信息页面 */}
                  <Route path="/privacy" element={<PrivacyPage />} />
                  <Route path="/terms" element={<TermsPage />} />
                  <Route path="/contact" element={<ContactPage />} />

                  {/* Protected Routes - Lazy loaded for code splitting */}
                  <Route
                    path="/main"
                    element={
                      <ProtectedRoute>
                        <MainDashboard />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/ai-select"
                    element={
                      <ProtectedRoute>
                        <LazyErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <AISelectPage />
                          </Suspense>
                        </LazyErrorBoundary>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/image-config"
                    element={
                      <ProtectedRoute>
                        <LazyErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <ImageConfigPage />
                          </Suspense>
                        </LazyErrorBoundary>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/sampling"
                    element={
                      <ProtectedRoute>
                        <LazyErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <SamplingPage />
                          </Suspense>
                        </LazyErrorBoundary>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/testing"
                    element={
                      <ProtectedRoute>
                        <LazyErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <TestingPage />
                          </Suspense>
                        </LazyErrorBoundary>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/profile"
                    element={
                      <ProtectedRoute>
                        <LazyErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <ProfilePage />
                          </Suspense>
                        </LazyErrorBoundary>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/invitation"
                    element={
                      <ProtectedRoute>
                        <LazyErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <InvitationPage />
                          </Suspense>
                        </LazyErrorBoundary>
                      </ProtectedRoute>
                    }
                  />

                  {/* Digit Recognition Routes */}
                  <Route
                    path="/digit-select"
                    element={
                      <ProtectedRoute>
                        <LazyErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <DigitSelectPage />
                          </Suspense>
                        </LazyErrorBoundary>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/digit-config"
                    element={
                      <ProtectedRoute>
                        <LazyErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <DigitConfigPage />
                          </Suspense>
                        </LazyErrorBoundary>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/digit-sampling"
                    element={
                      <ProtectedRoute>
                        <LazyErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <DigitSamplingPage />
                          </Suspense>
                        </LazyErrorBoundary>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/digit-testing"
                    element={
                      <ProtectedRoute>
                        <LazyErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <DigitTestingPage />
                          </Suspense>
                        </LazyErrorBoundary>
                      </ProtectedRoute>
                    }
                  />

                  {/* Ticket Routes */}
                  <Route
                    path="/tickets"
                    element={
                      <ProtectedRoute>
                        <LazyErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <TicketListPage />
                          </Suspense>
                        </LazyErrorBoundary>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/tickets/create"
                    element={
                      <ProtectedRoute>
                        <LazyErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <TicketCreatePage />
                          </Suspense>
                        </LazyErrorBoundary>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/tickets/:ticketId"
                    element={
                      <ProtectedRoute>
                        <LazyErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <TicketDetailPage />
                          </Suspense>
                        </LazyErrorBoundary>
                      </ProtectedRoute>
                    }
                  />

                  {/* Admin Ticket Routes */}
                  <Route
                    path="/admin/tickets"
                    element={
                      <ProtectedRoute>
                        <LazyErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <AdminTicketListPage />
                          </Suspense>
                        </LazyErrorBoundary>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/admin/tickets/:ticketId"
                    element={
                      <ProtectedRoute>
                        <LazyErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <AdminTicketDetailPage />
                          </Suspense>
                        </LazyErrorBoundary>
                      </ProtectedRoute>
                    }
                  />

                  {/* Notification Center Route */}
                  <Route
                    path="/notifications"
                    element={
                      <ProtectedRoute>
                        <LazyErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <NotificationCenterPage />
                          </Suspense>
                        </LazyErrorBoundary>
                      </ProtectedRoute>
                    }
                  />

                  {/* Admin Announcement Routes */}
                  <Route
                    path="/admin/announcements"
                    element={
                      <ProtectedRoute>
                        <LazyErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <AdminAnnouncementListPage />
                          </Suspense>
                        </LazyErrorBoundary>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/admin/announcements/create"
                    element={
                      <ProtectedRoute>
                        <LazyErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <AdminAnnouncementFormPage />
                          </Suspense>
                        </LazyErrorBoundary>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/admin/announcements/:id/edit"
                    element={
                      <ProtectedRoute>
                        <LazyErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <AdminAnnouncementFormPage />
                          </Suspense>
                        </LazyErrorBoundary>
                      </ProtectedRoute>
                    }
                  />

                  {/* Catch-all */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </main>

              <Footer />
            </div>
          }
        />
      </Routes>
    </Router>
  );
};